/**
 * feat-259 AC10 — the in-place row overlay, applied in ONE place.
 *
 * An auto-save does not reload (AT10), so the `content` prop stays stale for
 * exactly the items the editor just changed. `QuizContentManagement` keeps an
 * overlay of acknowledged writes, keyed `"<kind>:<id>"`, and lays it over
 * `content.lessons` on the way down. Everything downstream — the tree, the
 * middle column, the detail panel, and BOTH publish-guard call sites — then
 * reads one tree.
 *
 * WHY THE WHOLE LIST AND NOT JUST THE SELECTED DRILL (`qa` Finding 8). The
 * overlay used to be applied inside the `selectedDrill` memo, so the drill pill
 * read patched items and the lesson pill read the raw prop. `reportForUnit`
 * walks every drill in the lesson, so an overlay scoped to the selected drill
 * cannot cover it: the lesson pill published empty content with no confirmation
 * at all, and warned about items the editor had just filled in. Applying it at
 * the source is what makes the two pills unable to disagree — there is no second
 * place for a consumer to read from.
 *
 * The keys are globally unique row ids, not per-drill indexes, so laying the
 * same overlay over every drill is unambiguous.
 *
 * IDENTITY IS PRESERVED WHERE NOTHING CHANGED: a list, drill, or unit no patch
 * touches comes back as the very same object. `applyRowPatches` runs on every
 * render of a page that can hold a whole tenant's curriculum, and returning
 * fresh objects for untouched rows would re-render the tree on every keystroke's
 * save.
 */

import type { QuizLesson, QuizUnit } from '../../../services/quizContentAdminService';

/** `"<kind>:<id>"` → the fields the server has acknowledged since the last load. */
export type RowPatches = Record<string, Record<string, unknown>>;

/** The four item kinds a drill holds, paired with the field on `QuizLesson` that
 *  holds them. Adding a fifth item type means adding it HERE — the overlay is
 *  the single application point, so a type missing from this list is a type
 *  whose edits the publish guard cannot see. */
const ITEM_LISTS: ReadonlyArray<{ kind: string; field: keyof QuizLesson }> = [
  { kind: 'question', field: 'question_items' },
  { kind: 'rehearsal', field: 'rehearsal_items' },
  { kind: 'video', field: 'video_items' },
  { kind: 'reading', field: 'reading_items' },
];

function patchList<T extends { id: string }>(
  kind: string,
  list: T[] | undefined,
  patches: RowPatches,
): T[] | undefined {
  if (!list) return list;
  let touched = false;
  const next = list.map((item) => {
    const patch = patches[`${kind}:${item.id}`];
    if (!patch) return item;
    touched = true;
    return { ...item, ...patch } as T;
  });
  return touched ? next : list;
}

export function applyRowPatchesToDrill(drill: QuizLesson, patches: RowPatches): QuizLesson {
  let touched = false;
  const next: Record<string, unknown> = {};
  ITEM_LISTS.forEach(({ kind, field }) => {
    const list = drill[field] as unknown as Array<{ id: string }> | undefined;
    const patched = patchList(kind, list, patches);
    if (patched !== list) {
      touched = true;
      next[field as string] = patched;
    }
  });
  return touched ? ({ ...drill, ...next } as QuizLesson) : drill;
}

export function applyRowPatches(units: QuizUnit[], patches: RowPatches): QuizUnit[] {
  if (Object.keys(patches).length === 0) return units;
  let anyUnitTouched = false;
  const next = units.map((unit) => {
    let anyDrillTouched = false;
    const drills = (unit.drills ?? []).map((drill) => {
      const patched = applyRowPatchesToDrill(drill, patches);
      if (patched !== drill) anyDrillTouched = true;
      return patched;
    });
    if (!anyDrillTouched) return unit;
    anyUnitTouched = true;
    return { ...unit, drills };
  });
  return anyUnitTouched ? next : units;
}
